import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/shadcn-card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ArrowRight, Trophy, Users, Calendar, Star } from 'lucide-react';

// This component shows how to migrate your existing components to shadcn/ui

const MigrationExample = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-white">
            🔄 Migration Guide: Before vs After
          </h1>
          <p className="text-xl text-slate-300">
            See how your existing components can be improved with shadcn/ui
          </p>
        </div>

        {/* Button Migration Example */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Button Migration</CardTitle>
            <CardDescription>
              Replace your custom buttons with shadcn Button component
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Before */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">❌ Before (Your current style):</h3>
              <div className="bg-slate-900/50 p-4 rounded-lg">
                <code className="text-green-400 text-sm block mb-3">
                  {`<button className="btn-primary">Click me</button>`}
                </code>
                <button className="btn-primary">
                  Old Button Style
                </button>
              </div>
            </div>

            {/* After */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">✅ After (shadcn/ui):</h3>
              <div className="bg-slate-900/50 p-4 rounded-lg">
                <code className="text-green-400 text-sm block mb-3">
                  {`<Button variant="default" size="default">Click me</Button>`}
                </code>
                <div className="flex gap-2 flex-wrap">
                  <Button>Default</Button>
                  <Button variant="secondary">Secondary</Button>
                  <Button variant="outline">Outline</Button>
                  <Button variant="ghost">Ghost</Button>
                  <Button size="sm">Small</Button>
                  <Button size="lg">Large</Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card Migration Example */}
        <div className="grid md:grid-cols-2 gap-6">
          
          {/* Before Card */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">❌ Before (Your current cards):</h3>
            <div className="card">
              <div className="card-header">
                <h3 className="text-xl font-semibold text-white">Challenge Title</h3>
                <p className="text-gray-400">Challenge description here</p>
              </div>
              <div className="card-body">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Participants</span>
                    <span className="text-white">1,247</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Prize</span>
                    <span className="text-yellow-500">$10,000</span>
                  </div>
                </div>
              </div>
              <div className="card-footer">
                <button className="btn-primary w-full">Join Challenge</button>
              </div>
            </div>
          </div>

          {/* After Card */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">✅ After (shadcn/ui cards):</h3>
            <Card className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-colors">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    Challenge Title
                  </CardTitle>
                  <Badge variant="secondary">Active</Badge>
                </div>
                <CardDescription>
                  Enhanced challenge description with better typography
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400 flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Participants
                    </span>
                    <span className="text-white font-medium">1,247</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400 flex items-center gap-1">
                      <Trophy className="h-4 w-4" />
                      Prize
                    </span>
                    <span className="text-yellow-500 font-medium">$10,000</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Deadline
                    </span>
                    <span className="text-red-400 font-medium">5 days</span>
                  </div>
                </div>
              </CardContent>
              <CardContent className="pt-0">
                <Button className="w-full">
                  <Star className="h-4 w-4 mr-2" />
                  Join Challenge
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Input Migration Example */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Input Migration</CardTitle>
            <CardDescription>
              Better form inputs with consistent styling
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Before */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">❌ Before:</h3>
              <div className="bg-slate-900/50 p-4 rounded-lg">
                <input 
                  type="text" 
                  placeholder="Search challenges..." 
                  className="input w-full"
                />
              </div>
            </div>

            {/* After */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">✅ After:</h3>
              <div className="bg-slate-900/50 p-4 rounded-lg">
                <Input 
                  placeholder="Search challenges with better styling..." 
                  className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-400"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Migration Steps */}
        <Card className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border-blue-700">
          <CardHeader>
            <CardTitle className="text-white text-2xl">
              🚀 How to Migrate Your Components
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                <div>
                  <h4 className="text-white font-medium">Replace imports</h4>
                  <p className="text-slate-300 text-sm">Change from your custom components to shadcn imports</p>
                  <code className="text-green-400 text-xs block mt-1">
                    {`import { Button } from '@/components/ui/button'`}
                  </code>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                <div>
                  <h4 className="text-white font-medium">Update component usage</h4>
                  <p className="text-slate-300 text-sm">Use the new component API with variants and sizes</p>
                  <code className="text-green-400 text-xs block mt-1">
                    {`<Button variant="outline" size="lg">Click me</Button>`}
                  </code>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                <div>
                  <h4 className="text-white font-medium">Customize with CSS variables</h4>
                  <p className="text-slate-300 text-sm">Maintain your brand colors using the CSS variable system</p>
                  <code className="text-green-400 text-xs block mt-1">
                    {`--primary: 45 100% 51%; /* Your gold color */`}
                  </code>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                <div>
                  <h4 className="text-white font-medium">Test and iterate</h4>
                  <p className="text-slate-300 text-sm">Gradually migrate components and test functionality</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">🎯 Recommended Next Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="text-white font-medium">Add More Components:</h4>
                <div className="text-sm text-slate-300 space-y-1">
                  <div>• <code className="text-green-400">npx shadcn@latest add dropdown-menu</code></div>
                  <div>• <code className="text-green-400">npx shadcn@latest add tabs</code></div>
                  <div>• <code className="text-green-400">npx shadcn@latest add tooltip</code></div>
                  <div>• <code className="text-green-400">npx shadcn@latest add form</code></div>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="text-white font-medium">Customize Theme:</h4>
                <div className="text-sm text-slate-300 space-y-1">
                  <div>• Update CSS variables in index.css</div>
                  <div>• Modify tailwind.config.js colors</div>
                  <div>• Add custom component variants</div>
                  <div>• Test dark/light mode support</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default MigrationExample;